import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { LoanAgreementData } from '../../../services/schemas/loanServiceSchemas/loanAgreementData';

interface LoanAgreementFormProps {
    initialData: LoanAgreementData;
    onSubmit: (data: LoanAgreementData) => void;
    onCancel: () => void;
    onSkip?: () => void;
    isGenerating?: boolean;
    isIndividual?: boolean;
}

const LoanAgreementForm: React.FC<LoanAgreementFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    onSkip,
    isGenerating = false,
    isIndividual = false
}) => {
    const [formData, setFormData] = useState<LoanAgreementData>(initialData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const individualBlockers = [
        !formData.clientName?.trim() && 'Borrower name is required',
        !formData.loanApplicationId?.trim() && 'Loan application ID is required',
        formData.interestRate !== undefined && formData.interestRate > 100 && 'Interest rate cannot exceed 100%',
        formData.summaryInterestRate !== undefined && formData.summaryInterestRate > 100 && 'Summary interest rate cannot exceed 100%'
    ].filter(Boolean);

    // Check if any collateral information exists
    const hasCollateralInfo = () => {
        return !!(
            formData.collateralType ||
            formData.collateralMake ||
            formData.collateralModel ||
            formData.collateralYearOfManufacture ||
            formData.collateralRegistrationNumber ||
            formData.collateralMileage ||
            formData.collateralOwnerName
        );
    };

    useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        // Handle numeric fields
        if (['interestRate', 'loanDurationDays', 'summaryLoanAmount', 'summaryInterestRate', 
             'summaryInitiationFee', 'summaryMonthlyFee', 'summaryTotalRepayment', 
             'fieldsPopulated', 'totalAvailableFields'].includes(name)) {
            
            const numericValue = value === '' ? undefined : parseFloat(value);
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value || undefined
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Required fields
        if (!formData.clientName?.trim()) {
            newErrors.clientName = isIndividual ? 'Borrower name is required' : 'Client name is required';
        }

        if (!isIndividual && !formData.debtorName?.trim()) {
            newErrors.debtorName = 'Debtor name is required';
        }

        if (!formData.loanApplicationId?.trim()) {
            newErrors.loanApplicationId = 'Loan application ID is required';
        }

        // Validate percentage fields
        if (formData.interestRate !== undefined && formData.interestRate > 100) {
            newErrors.interestRate = 'Interest rate cannot exceed 100%';
        }

        if (formData.summaryInterestRate !== undefined && formData.summaryInterestRate > 100) {
            newErrors.summaryInterestRate = 'Summary interest rate cannot exceed 100%';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSubmit(isIndividual ? {
                ...formData,
                debtorName: formData.clientName?.trim(),
                signatureDebtorName: formData.clientName?.trim()
            } : formData);
        } else {
            toast.error('Please correct the errors in the form');
        }
    };

    return (
        <>
            <h6 className="form-section-title">Loan Agreement Data Verification</h6>
            <p className="text-muted mb-4">
                Review and edit the loan agreement data below. All fields marked with * are required.
            </p>

            <form onSubmit={handleSubmit}>
                <div className="row g-3">
                    {/* Client Information Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Client Information</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="clientName">{isIndividual ? 'Borrower Name' : 'Client Name'} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.clientName ? 'is-invalid' : ''}`}
                                        id="clientName"
                                        name="clientName"
                                        value={formData.clientName || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter client name"
                                        disabled={isGenerating}
                                    />
                                    {errors.clientName && (
                                        <div className="invalid-feedback">{errors.clientName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="registrationNumber">{isIndividual ? 'Identity Number' : 'Registration Number'}</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.registrationNumber ? 'is-invalid' : ''}`}
                                        id="registrationNumber"
                                        name="registrationNumber"
                                        value={formData.registrationNumber || ''}
                                        onChange={handleInputChange}
                                        placeholder={isIndividual ? 'Enter identity number' : 'Enter registration number'}
                                        disabled={isGenerating}
                                    />
                                    {errors.registrationNumber && (
                                        <div className="invalid-feedback">{errors.registrationNumber}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorName">{isIndividual ? 'Borrower Name on Agreement' : 'Debtor Name'} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorName ? 'is-invalid' : ''}`}
                                        id="debtorName"
                                        name="debtorName"
                                        value={(isIndividual ? formData.clientName : formData.debtorName) || ''}
                                        readOnly={isIndividual}
                                        onChange={handleInputChange}
                                        placeholder="Enter debtor name"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorName && (
                                        <div className="invalid-feedback">{errors.debtorName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="applicationNumber">Application Number</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.applicationNumber ? 'is-invalid' : ''}`}
                                        id="applicationNumber"
                                        name="applicationNumber"
                                        value={formData.applicationNumber || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter application number"
                                        disabled={isGenerating}
                                    />
                                    {errors.applicationNumber && (
                                        <div className="invalid-feedback">{errors.applicationNumber}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loan Details Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Loan Details</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="loanAmountDescription">Loan Amount Description</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.loanAmountDescription ? 'is-invalid' : ''}`}
                                        id="loanAmountDescription"
                                        name="loanAmountDescription"
                                        value={formData.loanAmountDescription || ''}
                                        onChange={handleInputChange}
                                        placeholder="e.g., R 45,000.00"
                                        disabled={isGenerating}
                                    />
                                    {errors.loanAmountDescription && (
                                        <div className="invalid-feedback">{errors.loanAmountDescription}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="commencementDate">Commencement Date</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.commencementDate ? 'is-invalid' : ''}`}
                                        id="commencementDate"
                                        name="commencementDate"
                                        value={formData.commencementDate || ''}
                                        readOnly
                                        disabled={isGenerating}
                                    />
                                    <small className="form-text text-muted">From the loan application: the contract always uses the commencement date captured there.</small>
                                    {errors.commencementDate && (
                                        <div className="invalid-feedback">{errors.commencementDate}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorAmountDescription">Debtor Amount Description</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorAmountDescription ? 'is-invalid' : ''}`}
                                        id="debtorAmountDescription"
                                        name="debtorAmountDescription"
                                        value={formData.debtorAmountDescription || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter debtor amount description"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorAmountDescription && (
                                        <div className="invalid-feedback">{errors.debtorAmountDescription}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorCommencementDate">Debtor Commencement Date</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.debtorCommencementDate ? 'is-invalid' : ''}`}
                                        id="debtorCommencementDate"
                                        name="debtorCommencementDate"
                                        value={formData.debtorCommencementDate || ''}
                                        readOnly
                                        disabled={isGenerating}
                                    />
                                    <small className="form-text text-muted">From the loan application: the contract always uses the commencement date captured there.</small>
                                    {errors.debtorCommencementDate && (
                                        <div className="invalid-feedback">{errors.debtorCommencementDate}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Interest and Repayment Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Interest and Repayment Details</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="interestRate">Interest Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.interestRate ? 'is-invalid' : ''}`}
                                        id="interestRate"
                                        name="interestRate"
                                        value={formData.interestRate || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter interest rate"
                                        disabled={isGenerating}
                                    />
                                    {errors.interestRate && (
                                        <div className="invalid-feedback">{errors.interestRate}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="loanDurationDays">Loan Duration (Days)</label>
                                    <input
                                        type="number"
                                        className={`form-control ${errors.loanDurationDays ? 'is-invalid' : ''}`}
                                        id="loanDurationDays"
                                        name="loanDurationDays"
                                        value={formData.loanDurationDays || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter loan duration in days"
                                        disabled={isGenerating}
                                    />
                                    {errors.loanDurationDays && (
                                        <div className="invalid-feedback">{errors.loanDurationDays}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="loanDueDate">Loan Due Date</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.loanDueDate ? 'is-invalid' : ''}`}
                                        id="loanDueDate"
                                        name="loanDueDate"
                                        value={formData.loanDueDate || ''}
                                        onChange={handleInputChange}
                                        disabled={isGenerating}
                                    />
                                    {errors.loanDueDate && (
                                        <div className="invalid-feedback">{errors.loanDueDate}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="loanRepaymentReference">Loan Repayment Reference</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.loanRepaymentReference ? 'is-invalid' : ''}`}
                                        id="loanRepaymentReference"
                                        name="loanRepaymentReference"
                                        value={formData.loanRepaymentReference || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter repayment reference"
                                        disabled={isGenerating}
                                    />
                                    {errors.loanRepaymentReference && (
                                        <div className="invalid-feedback">{errors.loanRepaymentReference}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="loanReference">Loan Reference</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.loanReference ? 'is-invalid' : ''}`}
                                        id="loanReference"
                                        name="loanReference"
                                        value={formData.loanReference || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter loan reference"
                                        disabled={isGenerating}
                                    />
                                    {errors.loanReference && (
                                        <div className="invalid-feedback">{errors.loanReference}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Collateral Information Section - Only show if collateral data exists */}
                    {hasCollateralInfo() && (
                        <div className="col-12">
                            <h6 className="form-section-title">Collateral Information</h6>
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralType">Collateral Type</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralType ? 'is-invalid' : ''}`}
                                            id="collateralType"
                                            name="collateralType"
                                            value={formData.collateralType || ''}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Vehicle"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralType && (
                                            <div className="invalid-feedback">{errors.collateralType}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralMake">Vehicle Make</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralMake ? 'is-invalid' : ''}`}
                                            id="collateralMake"
                                            name="collateralMake"
                                            value={formData.collateralMake || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter vehicle make"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralMake && (
                                            <div className="invalid-feedback">{errors.collateralMake}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralModel">Vehicle Model</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralModel ? 'is-invalid' : ''}`}
                                            id="collateralModel"
                                            name="collateralModel"
                                            value={formData.collateralModel || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter vehicle model"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralModel && (
                                            <div className="invalid-feedback">{errors.collateralModel}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralYearOfManufacture">Year of Manufacture</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralYearOfManufacture ? 'is-invalid' : ''}`}
                                            id="collateralYearOfManufacture"
                                            name="collateralYearOfManufacture"
                                            value={formData.collateralYearOfManufacture || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter year of manufacture"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralYearOfManufacture && (
                                            <div className="invalid-feedback">{errors.collateralYearOfManufacture}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralRegistrationNumber">Vehicle Registration Number</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralRegistrationNumber ? 'is-invalid' : ''}`}
                                            id="collateralRegistrationNumber"
                                            name="collateralRegistrationNumber"
                                            value={formData.collateralRegistrationNumber || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter vehicle registration number"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralRegistrationNumber && (
                                            <div className="invalid-feedback">{errors.collateralRegistrationNumber}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="collateralMileage">Vehicle Mileage</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralMileage ? 'is-invalid' : ''}`}
                                            id="collateralMileage"
                                            name="collateralMileage"
                                            value={formData.collateralMileage || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter vehicle mileage"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralMileage && (
                                            <div className="invalid-feedback">{errors.collateralMileage}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="collateralOwnerName">Owner Name</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.collateralOwnerName ? 'is-invalid' : ''}`}
                                            id="collateralOwnerName"
                                            name="collateralOwnerName"
                                            value={formData.collateralOwnerName || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter owner name"
                                            disabled={isGenerating}
                                        />
                                        {errors.collateralOwnerName && (
                                            <div className="invalid-feedback">{errors.collateralOwnerName}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Debtor Address Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Debtor Address Information</h6>
                        <div className="row g-3">
                            <div className="col-12">
                                <div className="form-group">
                                    <label htmlFor="debtorsAddressLine1">Address Line 1</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsAddressLine1 ? 'is-invalid' : ''}`}
                                        id="debtorsAddressLine1"
                                        name="debtorsAddressLine1"
                                        value={formData.debtorsAddressLine1 || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter address line 1"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsAddressLine1 && (
                                        <div className="invalid-feedback">{errors.debtorsAddressLine1}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsAddressLine2">Address Line 2</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsAddressLine2 ? 'is-invalid' : ''}`}
                                        id="debtorsAddressLine2"
                                        name="debtorsAddressLine2"
                                        value={formData.debtorsAddressLine2 || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter address line 2"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsAddressLine2 && (
                                        <div className="invalid-feedback">{errors.debtorsAddressLine2}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsAddressLine3">Address Line 3</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsAddressLine3 ? 'is-invalid' : ''}`}
                                        id="debtorsAddressLine3"
                                        name="debtorsAddressLine3"
                                        value={formData.debtorsAddressLine3 || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter address line 3"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsAddressLine3 && (
                                        <div className="invalid-feedback">{errors.debtorsAddressLine3}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Banking Information Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Banking Information</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsAccountHolder">Account Holder Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsAccountHolder ? 'is-invalid' : ''}`}
                                        id="debtorsAccountHolder"
                                        name="debtorsAccountHolder"
                                        value={formData.debtorsAccountHolder || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter account holder name"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsAccountHolder && (
                                        <div className="invalid-feedback">{errors.debtorsAccountHolder}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsBankName">Bank Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsBankName ? 'is-invalid' : ''}`}
                                        id="debtorsBankName"
                                        name="debtorsBankName"
                                        value={formData.debtorsBankName || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter bank name"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsBankName && (
                                        <div className="invalid-feedback">{errors.debtorsBankName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsAccountNumber">Account Number</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsAccountNumber ? 'is-invalid' : ''}`}
                                        id="debtorsAccountNumber"
                                        name="debtorsAccountNumber"
                                        value={formData.debtorsAccountNumber || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter account number"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsAccountNumber && (
                                        <div className="invalid-feedback">{errors.debtorsAccountNumber}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="debtorsRepaymentReference">Repayment Reference</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.debtorsRepaymentReference ? 'is-invalid' : ''}`}
                                        id="debtorsRepaymentReference"
                                        name="debtorsRepaymentReference"
                                        value={formData.debtorsRepaymentReference || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter repayment reference"
                                        disabled={isGenerating}
                                    />
                                    {errors.debtorsRepaymentReference && (
                                        <div className="invalid-feedback">{errors.debtorsRepaymentReference}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Summary Information</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryLoanAmount">Summary Loan Amount (R)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.summaryLoanAmount ? 'is-invalid' : ''}`}
                                        id="summaryLoanAmount"
                                        name="summaryLoanAmount"
                                        value={formData.summaryLoanAmount || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter summary loan amount"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryLoanAmount && (
                                        <div className="invalid-feedback">{errors.summaryLoanAmount}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryInterestRate">Summary Interest Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.summaryInterestRate ? 'is-invalid' : ''}`}
                                        id="summaryInterestRate"
                                        name="summaryInterestRate"
                                        value={formData.summaryInterestRate || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter summary interest rate"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryInterestRate && (
                                        <div className="invalid-feedback">{errors.summaryInterestRate}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryInstallmentFrequency">Installment Frequency</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.summaryInstallmentFrequency ? 'is-invalid' : ''}`}
                                        id="summaryInstallmentFrequency"
                                        name="summaryInstallmentFrequency"
                                        value={formData.summaryInstallmentFrequency || ''}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Monthly"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryInstallmentFrequency && (
                                        <div className="invalid-feedback">{errors.summaryInstallmentFrequency}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryCommencementDate">Summary Commencement Date</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.summaryCommencementDate ? 'is-invalid' : ''}`}
                                        id="summaryCommencementDate"
                                        name="summaryCommencementDate"
                                        value={formData.summaryCommencementDate || ''}
                                        readOnly
                                        disabled={isGenerating}
                                    />
                                    <small className="form-text text-muted">From the loan application: the contract always uses the commencement date captured there.</small>
                                    {errors.summaryCommencementDate && (
                                        <div className="invalid-feedback">{errors.summaryCommencementDate}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryInitiationFee">Initiation Fee (R)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.summaryInitiationFee ? 'is-invalid' : ''}`}
                                        id="summaryInitiationFee"
                                        name="summaryInitiationFee"
                                        value={formData.summaryInitiationFee || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter initiation fee"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryInitiationFee && (
                                        <div className="invalid-feedback">{errors.summaryInitiationFee}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryLoanTerm">Loan Term</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.summaryLoanTerm ? 'is-invalid' : ''}`}
                                        id="summaryLoanTerm"
                                        name="summaryLoanTerm"
                                        value={formData.summaryLoanTerm || ''}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 1 year, 6 months"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryLoanTerm && (
                                        <div className="invalid-feedback">{errors.summaryLoanTerm}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryMonthlyFee">Monthly Fee (R)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.summaryMonthlyFee ? 'is-invalid' : ''}`}
                                        id="summaryMonthlyFee"
                                        name="summaryMonthlyFee"
                                        value={formData.summaryMonthlyFee || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter monthly fee"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryMonthlyFee && (
                                        <div className="invalid-feedback">{errors.summaryMonthlyFee}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryTotalRepayment">Total Repayment (R)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`form-control ${errors.summaryTotalRepayment ? 'is-invalid' : ''}`}
                                        id="summaryTotalRepayment"
                                        name="summaryTotalRepayment"
                                        value={formData.summaryTotalRepayment || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter total repayment amount"
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryTotalRepayment && (
                                        <div className="invalid-feedback">{errors.summaryTotalRepayment}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="summaryLoanDueDate">Summary Loan Due Date</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.summaryLoanDueDate ? 'is-invalid' : ''}`}
                                        id="summaryLoanDueDate"
                                        name="summaryLoanDueDate"
                                        value={formData.summaryLoanDueDate || ''}
                                        onChange={handleInputChange}
                                        disabled={isGenerating}
                                    />
                                    {errors.summaryLoanDueDate && (
                                        <div className="invalid-feedback">{errors.summaryLoanDueDate}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Signature Information Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Signature Information</h6>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="signatureDebtorName">{isIndividual ? 'Borrower Name for Signature' : 'Debtor Name for Signature'}</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.signatureDebtorName ? 'is-invalid' : ''}`}
                                        id="signatureDebtorName"
                                        name="signatureDebtorName"
                                        value={(isIndividual ? formData.clientName : formData.signatureDebtorName) || ''}
                                        readOnly={isIndividual}
                                        onChange={handleInputChange}
                                        placeholder="Enter debtor name for signature"
                                        disabled={isGenerating}
                                    />
                                    {errors.signatureDebtorName && (
                                        <div className="invalid-feedback">{errors.signatureDebtorName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="signatureDebtorRep">Debtor Representative</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.signatureDebtorRep ? 'is-invalid' : ''}`}
                                        id="signatureDebtorRep"
                                        name="signatureDebtorRep"
                                        value={formData.signatureDebtorRep || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter debtor representative name"
                                        disabled={isGenerating}
                                    />
                                    {errors.signatureDebtorRep && (
                                        <div className="invalid-feedback">{errors.signatureDebtorRep}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Status and Actions */}
                    <div className="col-12">
                        {/* Data Status Alerts */}
                        {formData.validationWarnings && formData.validationWarnings.length > 0 && (
                            <div className="alert alert-warning mb-3">
                                <h6 className="alert-heading">Validation Warnings</h6>
                                <ul className="mb-0">
                                    {formData.validationWarnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!isIndividual && formData.missingOptionalFields && formData.missingOptionalFields.length > 0 && (
                            <div className="alert alert-info mb-3">
                                <h6 className="alert-heading">Missing Optional Fields</h6>
                                <ul className="mb-0">
                                    {formData.missingOptionalFields.map((field, index) => (
                                        <li key={index}>{field}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Form Completion Status */}
                        {isIndividual ? (
                            <div className={`alert ${individualBlockers.length ? 'alert-warning' : 'alert-info'} mb-4`} role="status">
                                {individualBlockers.length ? <>
                                    <strong>Complete these fields to continue:</strong>
                                    <ul className="mb-0">{individualBlockers.map((message, index) => <li key={index}>{message}</li>)}</ul>
                                </> : 'Required form fields are complete. Review the details, then generate the agreement PDF. This does not approve pricing or complete the agreement.'}
                            </div>
                        ) : <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded mb-4">
                            <div>
                                <strong>Form Completion Status:</strong>
                                <span className="ms-2">
                                    {formData.fieldsPopulated || 0} of {formData.totalAvailableFields || 0} fields populated
                                </span>
                            </div>
                            <div>
                                <span className={`badge ${formData.canGenerate ? 'bg-success' : 'bg-warning'}`}>
                                    {formData.canGenerate ? 'Ready to Generate' : 'Incomplete Data'}
                                </span>
                            </div>
                        </div>}

                        {/* Form Actions */}
                        <div className="d-flex justify-content-between gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onCancel}
                                disabled={isGenerating}
                            >
                                <i className="fa fa-times me-2"></i>
                                Cancel
                            </button>
                            <div className="d-flex gap-2">
                                {onSkip && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={onSkip}
                                        disabled={isGenerating}
                                    >
                                        <i className="fa fa-forward me-2"></i>
                                        Skip Generation
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Generating PDF...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-file-pdf me-2"></i>
                                            Generate Loan Agreement PDF
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default LoanAgreementForm;
